import { useEffect } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Notification } from "./types";
import { formatNotificationMessage } from "./notificationFormatters";

const notificationsQueryKey = (userId?: string) =>
  ["notifications", userId] as const;

async function fetchNotifications(userId: string): Promise<Notification[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  return ((data || []) as Notification[]).map(formatNotificationMessage);
}

// This hook is called independently by over a dozen components (sidebar,
// mobile header, account menu, notification bell, menu tray, ...), several
// of which are mounted simultaneously in the layout shell. React Query
// already dedupes the fetch/cache across all of them via the shared query
// key below, but a realtime channel still needs to exist only once per
// user — not once per mounted instance — hence the refcounted singleton.
const activeChannels = new Map<
  string,
  { channel: ReturnType<typeof supabase.channel>; refCount: number }
>();

function subscribeToNotifications(userId: string, queryClient: QueryClient) {
  const existing = activeChannels.get(userId);
  if (existing) {
    existing.refCount += 1;
    return () => unsubscribeFromNotifications(userId);
  }

  const channel = supabase
    .channel(`notifications:${userId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        const newNotification = formatNotificationMessage(
          payload.new as Notification,
        );
        queryClient.setQueryData<Notification[]>(
          notificationsQueryKey(userId),
          (prev) => (prev ? [newNotification, ...prev] : [newNotification]),
        );
      },
    )
    .subscribe();

  activeChannels.set(userId, { channel, refCount: 1 });
  return () => unsubscribeFromNotifications(userId);
}

function unsubscribeFromNotifications(userId: string) {
  const existing = activeChannels.get(userId);
  if (!existing) return;

  existing.refCount -= 1;
  if (existing.refCount <= 0) {
    supabase.removeChannel(existing.channel);
    activeChannels.delete(userId);
  }
}

export function useNotificationsData() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: notificationsQueryKey(user?.id),
    queryFn: () => fetchNotifications(user!.id),
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (!user?.id) return;
    return subscribeToNotifications(user.id, queryClient);
  }, [user?.id, queryClient]);

  const notifications: Notification[] | null = !user ? [] : (data ?? null);
  const unreadCount = notifications?.filter((n) => !n.is_read).length ?? 0;

  const setNotifications = (
    updater:
      | Notification[]
      | null
      | ((prev: Notification[] | null) => Notification[] | null),
  ) => {
    queryClient.setQueryData<Notification[] | null>(
      notificationsQueryKey(user?.id),
      (prev) =>
        typeof updater === "function" ? updater(prev ?? null) : updater,
    );
  };

  return {
    notifications,
    setNotifications,
    loading: user ? isLoading : false,
    error: queryError instanceof Error ? queryError : null,
    unreadCount,
    // unreadCount is now derived straight from `notifications`' is_read
    // flags above, so actions that used to call this just to decrement a
    // separately-tracked counter no longer need to do anything here —
    // updating notifications via setNotifications already keeps it in sync.
    setUnreadCount: () => {},
  };
}
